import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import Keycloak from "keycloak-js";
// Central point for every backend call the app makes — hooking in here
// means any API failure, from any page/resource, reaches Discord without
// needing to be at the client's machine or ask them to reproduce it.
// notifyDiscord redacts PII/financial fields before anything leaves the app.
import { notifyDiscord } from "./discordNotifier";
import { reportNetworkFailure, reportNetworkSuccess } from "./networkHealth";

export const axiosInstance = axios.create();

type RetriableRequest = InternalAxiosRequestConfig & { _retry?: boolean };

const requestContext = (originalRequest?: RetriableRequest) => ({
  url: `${originalRequest?.baseURL ?? ""}${originalRequest?.url ?? ""}`,
  method: originalRequest?.method?.toUpperCase(),
});

const escalateToMaintenance = () => {
  // infinite loop'u engellemek için kontrol
  if (window.location.pathname !== "/maintenance") {
    window.location.href = "/maintenance";
  }
};

// A single response-less error (CORS blip, one timed-out request, a dropped
// connection) used to redirect the WHOLE app to /maintenance instantly — a
// brand-new user's first session could hit that from one transient hiccup
// and look "broken" rather than just briefly degraded. Now it only shows a
// soft banner (see NetworkHealthBanner.tsx) immediately, and only escalates
// to the full-page redirect if failures are still happening after a short
// grace window AND enough of them happened — see networkHealth.ts for the
// exact rule (it also protects against a burst of simultaneous parallel
// requests all failing from the same single blip).
const handleNetworkError = (error: AxiosError) => {
  const { url, method } = requestContext(
    error.config as RetriableRequest | undefined,
  );
  console.error("CORS ERROR", { url, method, reason: error.message });

  reportNetworkFailure(escalateToMaintenance);

  return Promise.reject(error);
};

const handleTokenRefresh = async (
  error: AxiosError,
  keycloak: Keycloak,
  originalRequest: RetriableRequest,
) => {
  const { url, method } = requestContext(originalRequest);
  originalRequest._retry = true;

  try {
    await keycloak.updateToken(-1);
    originalRequest.headers.Authorization = `Bearer ${keycloak.token}`;
    return axios(originalRequest);
  } catch (refreshError) {
    console.error("Token refresh failed", { url, method, refreshError });
    keycloak.logout();
    return error;
  }
};

const handleSessionExpired = (
  keycloak: Keycloak,
  originalRequest: RetriableRequest,
) => {
  const { url, method } = requestContext(originalRequest);
  void notifyDiscord({
    title: "Frontend Auth — session expired, logging out",
    context: { url, method },
  });
  keycloak.logout();
};

const handleApiError = (
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  error: AxiosError<any> & { statusCode?: number },
  originalRequest: RetriableRequest,
) => {
  const { url, method } = requestContext(originalRequest);
  void notifyDiscord({
    title: `Frontend API — ${method ?? "request"} failed (${error.response?.status ?? "?"})`,
    context: { url, status: error.response?.status },
    request: originalRequest?.data,
    response: error.response?.data,
    reason: error.message,
  });

  // Get the error message from the response metadata
  // and set it as the error message to show in the UI
  if (
    error.response?.status === 400 &&
    error.response?.data?.metadata?.message
  ) {
    error.message = error.response.data.metadata.message;
    error.statusCode = error.response.status;
  }
};

export const addTokenRefreshInterceptors = (keycloak: Keycloak) => {
  // Request interceptor to refresh the token if it will be expired soon
  axiosInstance.interceptors.request.use(
    async (config) => {
      config.headers.Authorization = `Bearer ${keycloak.token}`;

      return config;
    },
    (error) => error,
  );

  axiosInstance.interceptors.response.use(
    (response) => {
      reportNetworkSuccess();
      return response;
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async (error: AxiosError<any>) => {
      const originalRequest = error.config as RetriableRequest;

      if (!error.response) {
        return handleNetworkError(error);
      }

      // A real HTTP response came back (even an error one) — connectivity to
      // the backend is fine, so this clears any network-error streak just
      // like a success would. Only the response-less branch above means
      // "couldn't reach the backend at all".
      reportNetworkSuccess();

      const isRetry = Boolean(originalRequest._retry);

      // If the error status is 401 and there is no originalRequest._retry flag,
      // it means the token has expired and we need to refresh it
      if (error.response?.status === 401 && !isRetry) {
        return handleTokenRefresh(error, keycloak, originalRequest);
      }

      // If the error status is 401 and the originalRequest._retry flag is set,
      // it means the token has expired and the refresh token request has failed
      // We need to logout the user
      if (error.response?.status === 401 && isRetry) {
        handleSessionExpired(keycloak, originalRequest);
        return error;
      }

      handleApiError(error, originalRequest);
      return error;
    },
  );
};

export const setAxiosLanguage = (lang: string) => {
  axiosInstance.defaults.headers.common["Accept-Language"] = lang;
};
